import React from 'react';
import { type TFunction } from 'react-i18next';
import {
  Controller,
  type Control,
  type FieldValues,
  type FieldErrors,
  type UseFormGetValues
} from 'react-hook-form';
import { Select } from '@deque/cauldron-react';

import type { SelectOption } from '../FieldMapping';
import { ProductNames } from '../../../../../../../common/constants';
import styles from './Field.css';

export interface FieldAssigmentProps {
  axeFields: SelectOption[];
  mappingFields: SelectOption[];
  index: number;
  control: Control<FieldValues, unknown>;
  getError: (
    field: {
      value: string;
    },
    fieldError: FieldErrors<FieldValues>,
    i: number
  ) => string | undefined;
  errors: FieldErrors<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  t: TFunction<'translation', undefined>;
}

const FieldAssigment = ({
  axeFields,
  mappingFields,
  index,
  control,
  getError,
  errors,
  getValues,
  t
}: FieldAssigmentProps) => {
  const mappingField = mappingFields.find(
    mappingFieldToCheck =>
      mappingFieldToCheck.key ===
      getValues().fieldMapping?.[index]?.mappingField // ? are needed for unit tests to pass, not needed in real usage
  );
  /* istanbul ignore next */
  const isEnum: boolean = mappingField?.fieldWithMeta?.isEnum === true;

  let spacer = <></>;
  let controller = <></>;
  if (mappingField && isEnum) {
    spacer = (
      <p className={styles.from} id={`from-${index}`}>
        {t(`set to`)}
      </p>
    );

    let enumValues: SelectOption[] | undefined =
      mappingField.fieldWithMeta?.field.allowedValues?.map(allowedValue => {
        return {
          key: allowedValue.id,
          label: allowedValue.value,
          value: allowedValue.id
        };
      });
    if (enumValues) {
      enumValues = [
        {
          key: '',
          label: '',
          value: ''
        },
        ...enumValues
      ];
    }

    controller = (
      <Controller
        name={`fieldMapping[${index}].axeField`}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            label={t(`Field value`)}
            options={enumValues}
            error={getError(field, errors, index)}
            id={`axe-field-${index}`}
            aria-label={t(`{{ axe }} field {{ index }}`, {
              axe: ProductNames.axe,
              index: index + 1
            })}
          />
        )}
      />
    );
  } else {
    spacer = (
      <p className={styles.from} id={`from-${index}`}>
        {t(`from`)}
      </p>
    );

    controller = (
      <Controller
        name={`fieldMapping[${index}].axeField`}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            label={t(`{{ axe }} Field`, {
              axe: ProductNames.axe
            })}
            options={axeFields}
            error={getError(field, errors, index)}
            id={`axe-field-${index}`}
            aria-label={t(`{{ axe }} field {{ index }}`, {
              axe: ProductNames.axe,
              index: index + 1
            })}
          />
        )}
      />
    );
  }

  return (
    <>
      {spacer}
      {controller}
    </>
  );
};

export default FieldAssigment;
